// Prime League match pages as the worker reads them (GET /api/pl/matches/:id), turned into the review's game shape.
import type { AnalysisGame, AnalysisSide, PlGame, PlMatch } from "../types";
import { championIdByName, getChampionName } from "./ddragon";

const ROLES = ["TOP", "JUNGLE", "MIDDLE", "BOTTOM", "UTILITY"];

/** Match ids out of pasted primeleague.gg match links (any text around them), de-duplicated, in order. */
export function plMatchIds(text: string): number[] {
	return [...new Set([...text.matchAll(/primeleague\.gg\/[a-z]{2}\/leagues\/matches\/(\d+)/g)].map(m => Number(m[1])))];
}

/** The tag of the team in the most matches (the one being reviewed); ties: the first. */
export function commonTeam(matches: PlMatch[]): string | null {
	const count = new Map<string, number>();
	for (const m of matches) for (const tag of Object.keys(m.teams)) count.set(tag, (count.get(tag) ?? 0) + 1);
	return [...count].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

/** A PL game seen from team `us`. Bans get their global draft turn (1-6 phase 1, 7-10 phase 2). */
export function toAnalysisGame(g: PlGame, us: string): AnalysisGame | null {
	const ourSide = g.blue.tag === us ? "blue" : g.red.tag === us ? "red" : null;
	if (!ourSide) return null;
	let turn = 0;
	const bans = g.draft.flatMap(d => (d.kind === "ban" ? [{ side: d.side, championId: championIdByName(d.champion), turn: ++turn }] : []));
	const side = (s: "blue" | "red"): AnalysisSide => {
		const players = g[s].players.map((p, i) => ({ ...p, role: ROLES[i] ?? "", championId: championIdByName(p.champion), championName: p.champion }));
		return {
			side: s,
			win: g[s].win,
			kills: players.reduce((a, p) => a + p.kills, 0),
			gold: 0,
			players,
			bans: bans.filter(b => b.side === s).map(({ championId, turn }) => ({ championId, turn })),
			styles: []
		};
	};
	return {
		matchId: g.matchId,
		date: g.start,
		duration: g.duration,
		draft: true,
		// in-client draft games (calibration) come with their bans only: no pick order to show
		order: g.draft.some(d => d.kind === "pick") ? g.draft.map(d => ({ kind: d.kind, side: d.side, championId: championIdByName(d.champion) })) : undefined,
		us: side(ourSide),
		them: side(ourSide === "blue" ? "red" : "blue")
	};
}

export interface ChampCount {
	championId: number;
	count: number;
	wins: number;
}
export interface DraftStats {
	/** games with a known pick order (group phase); bans also count the calibration games */
	ordered: number;
	firstPickBlue: ChampCount[];
	/** their first two picks on red (R1, R2) */
	firstPicksRed: ChampCount[];
	/** their picks 1-3 (first pick phase) and 4-5 (after the second ban phase: where counter picks go) */
	picks1: ChampCount[];
	picks2: ChampCount[];
	/** which role they lock in at which of their 5 picks, earliest first */
	roleOrder: { role: string; avg: number; first: number; last: number }[];
	bans1: ChampCount[];
	bans2: ChampCount[];
	against1: ChampCount[];
	against2: ChampCount[];
	/** bans against them that hit a champion this player played in these games */
	targeted: { riotId: string; champs: ChampCount[] }[];
}

const LIMIT = 15;
function counter() {
	const m = new Map<number, ChampCount>();
	return {
		add(championId: number, win: boolean) {
			if (!championId) return;
			const c = m.get(championId) ?? { championId, count: 0, wins: 0 };
			c.count++;
			if (win) c.wins++;
			m.set(championId, c);
		},
		top: () => [...m.values()].sort((a, b) => b.count - a.count || b.wins - a.wins).slice(0, LIMIT)
	};
}

/** Pure: the team's draft habits over these games (seen from `us`). */
export function draftStats(games: AnalysisGame[]): DraftStats {
	const firstBlue = counter(),
		firstRed = counter(),
		picks1 = counter(),
		picks2 = counter(),
		bans1 = counter(),
		bans2 = counter(),
		against1 = counter(),
		against2 = counter();
	const slots = new Map<string, number[]>();
	for (const g of games) {
		const win = g.us.win;
		for (const b of g.us.bans) (b.turn <= 6 ? bans1 : bans2).add(b.championId, win);
		for (const b of g.them.bans) (b.turn <= 6 ? against1 : against2).add(b.championId, win);
		const ours = g.order?.filter(d => d.kind === "pick" && d.side === g.us.side) ?? [];
		ours.forEach((d, i) => {
			(i < 3 ? picks1 : picks2).add(d.championId, win);
			if (g.us.side === "blue" ? i === 0 : i < 2) (g.us.side === "blue" ? firstBlue : firstRed).add(d.championId, win);
			const role = g.us.players.find(p => p.championId === d.championId)?.role;
			if (role) slots.set(role, [...(slots.get(role) ?? []), i + 1]);
		});
	}
	// a player's pool here = what they played in these games; a ban against them on it is aimed at them
	const pools = new Map<string, Set<number>>();
	for (const g of games) for (const p of g.us.players) pools.set(p.riotId, (pools.get(p.riotId) ?? new Set()).add(p.championId));
	const targeted = [...pools].flatMap(([riotId, pool]) => {
		const c = counter();
		for (const g of games) for (const b of g.them.bans) if (pool.has(b.championId)) c.add(b.championId, g.us.win);
		const champs = c.top();
		return champs.length ? [{ riotId, champs }] : [];
	});
	return {
		ordered: games.filter(g => g.order).length,
		firstPickBlue: firstBlue.top(),
		firstPicksRed: firstRed.top(),
		picks1: picks1.top(),
		picks2: picks2.top(),
		roleOrder: [...slots]
			.map(([role, s]) => ({ role, avg: s.reduce((a, x) => a + x, 0) / s.length, first: s.filter(x => x === 1).length, last: s.filter(x => x === 5).length }))
			.sort((a, b) => a.avg - b.avg),
		bans1: bans1.top(),
		bans2: bans2.top(),
		against1: against1.top(),
		against2: against2.top(),
		targeted: targeted.sort((a, b) => b.champs.reduce((n, c) => n + c.count, 0) - a.champs.reduce((n, c) => n + c.count, 0))
	};
}

const ROLE_NAME: Record<string, string> = { TOP: "Top", JUNGLE: "Jungle", MIDDLE: "Mid", BOTTOM: "Bot", UTILITY: "Support" };

/** The few things worth knowing before the draft, as sentences. Only patterns that held in at least half the games. */
export function keyPoints(d: DraftStats, games: AnalysisGame[]): string[] {
	const out: string[] = [];
	const n = d.ordered;
	if (n >= 2) {
		const first = [...d.roleOrder].sort((a, b) => b.first - a.first)[0];
		if (first && first.first * 2 >= n) out.push(`They lock in ${ROLE_NAME[first.role] ?? first.role} first in ${first.first} of ${n} games.`);
		const last = [...d.roleOrder].sort((a, b) => b.last - a.last)[0];
		if (last && last.last * 2 >= n) out.push(`${ROLE_NAME[last.role] ?? last.role} is their last pick in ${last.last} of ${n} games: that is where a counter pick comes.`);
	}
	// per game, so a champion counts once even if both phases had it
	const top = (ids: (g: AnalysisGame) => number[]) => {
		const c = new Map<number, number>();
		for (const g of games) for (const id of new Set(ids(g))) if (id) c.set(id, (c.get(id) ?? 0) + 1);
		return [...c].sort((a, b) => b[1] - a[1])[0];
	};
	const against = top(g => g.them.bans.map(b => b.championId));
	if (against && against[1] >= 2 && against[1] * 2 >= games.length) out.push(`${getChampionName(against[0])} is banned against them in ${against[1]} of ${games.length} games.`);
	const ban = top(g => g.us.bans.map(b => b.championId));
	if (ban && ban[1] >= 3) out.push(`Their most common ban is ${getChampionName(ban[0])} (${ban[1]} of ${games.length} games).`);
	return out;
}

export interface GamePlan {
	/** their most played champions (2+ games), with the player who plays each most */
	bans: (ChampCount & { riotId: string })[];
	/** the player with the biggest share of the team's kills */
	carry: { riotId: string; role: string; killShare: number; kda: number } | null;
	/** the role furthest behind its lane opponent in gold at 15:00 (Riot timeline), averaged over `games` games */
	weak: { riotId: string; role: string; goldDiff: number; csDiff: number; games: number } | null;
}

const mostOf = (m: Map<string, number>) => [...m].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "";
const bump = (m: Map<string, number>, k: string) => m.set(k, (m.get(k) ?? 0) + 1);

/** Pure: what to do against this team. Carry and weak lane need 2+ games and a player or role in at least half of them. */
export function gamePlan(games: AnalysisGame[]): GamePlan {
	const n = games.length;
	const champs = new Map<number, ChampCount & { by: Map<string, number> }>();
	const players = new Map<string, { games: number; kills: number; deaths: number; assists: number; teamKills: number; roles: Map<string, number> }>();
	const lanes = new Map<string, { games: number; goldDiff: number; csDiff: number; by: Map<string, number> }>();
	for (const g of games)
		for (const p of g.us.players) {
			if (p.championId) {
				const c = champs.get(p.championId) ?? { championId: p.championId, count: 0, wins: 0, by: new Map() };
				c.count++;
				if (g.us.win) c.wins++;
				bump(c.by, p.riotId);
				champs.set(p.championId, c);
			}
			const s = players.get(p.riotId) ?? { games: 0, kills: 0, deaths: 0, assists: 0, teamKills: 0, roles: new Map() };
			s.games++;
			s.kills += p.kills;
			s.deaths += p.deaths;
			s.assists += p.assists;
			s.teamKills += g.us.kills;
			bump(s.roles, p.role);
			players.set(p.riotId, s);
			const opp = g.them.players.find(o => o.role === p.role);
			if (!p.role || !p.at15 || !opp?.at15) continue;
			const l = lanes.get(p.role) ?? { games: 0, goldDiff: 0, csDiff: 0, by: new Map() };
			l.games++;
			l.goldDiff += p.at15.gold - opp.at15.gold;
			l.csDiff += p.at15.cs - opp.at15.cs;
			bump(l.by, p.riotId);
			lanes.set(p.role, l);
		}
	const regular = (games: number) => n >= 2 && games * 2 >= n;
	const carry = [...players]
		.filter(([, s]) => regular(s.games))
		.map(([riotId, s]) => ({ riotId, role: mostOf(s.roles), killShare: s.teamKills ? s.kills / s.teamKills : 0, kda: (s.kills + s.assists) / Math.max(1, s.deaths) }))
		.sort((a, b) => b.killShare - a.killShare)[0];
	const weak = [...lanes]
		.filter(([, l]) => regular(l.games))
		.map(([role, l]) => ({ riotId: mostOf(l.by), role, goldDiff: l.goldDiff / l.games, csDiff: l.csDiff / l.games, games: l.games }))
		.sort((a, b) => a.goldDiff - b.goldDiff)[0];
	return {
		bans: [...champs.values()]
			.filter(c => c.count >= 2)
			.sort((a, b) => b.count - a.count || b.wins - a.wins)
			.slice(0, 3)
			.map(({ by, ...c }) => ({ ...c, riotId: mostOf(by) })),
		carry: carry ?? null,
		weak: weak && weak.goldDiff < 0 ? weak : null
	};
}

export interface EarlyPlan {
	/** games with a Riot timeline: everything below is out of these */
	games: number;
	/** where the jungler is at 2:00, the lanes of the first ganks (most first) and their average time in seconds */
	jungle: { top: number; bot: number; ganks: { lane: string; count: number }[]; gankT: number | null };
	/** fights before 1:30 */
	lvl1: { won: number; lost: number };
	/** team gold lead at 15:00 and what came of it */
	tempo: { games: number; ahead: number; aheadWins: number; behind: number; behindWins: number; avg: number } | null;
	/** the player who dies first most often; ganked = by the enemy jungler; deaths10 = their deaths before 10:00 a game */
	firstDeath: { riotId: string; role: string; count: number; ganked: number; deaths10: number } | null;
	/** their first epic monster by type (most first); the game's first dragon: how often theirs and when (seconds) */
	objectives: { first: { type: string; count: number }[]; dragons: number; dragonGames: number; dragonT: number | null };
	/** the jungler's minutes 2-14 over all games: top half, bottom half, in the enemy's jungle */
	presence: { minutes: number; top: number; bot: number; invading: number } | null;
	/** games where the bot lane went top early */
	laneSwaps: number;
	/** mid and support: games with 2+ minutes away from lane before 14:00, average minutes away, average first roam minute */
	roams: { role: string; games: number; minutes: number; first: number | null }[];
}

const sum = (xs: number[]) => xs.reduce((a, x) => a + x, 0);
const avg = (xs: number[]) => (xs.length ? sum(xs) / xs.length : 0);

/** Pure: the early-game habits over the games with a timeline. The first death needs 2+ games and 2+ first deaths. */
export function earlyPlan(games: AnalysisGame[]): EarlyPlan {
	const tl = games.filter(g => g.early);
	const ganks = new Map<string, number[]>();
	const deaths = new Map<string, { role: string; count: number; ganked: number }>();
	const first = new Map<string, number>();
	for (const g of tl) {
		const e = g.early!;
		if (e.firstGank) ganks.set(e.firstGank.lane, [...(ganks.get(e.firstGank.lane) ?? []), e.firstGank.t]);
		if (e.firstDeath) {
			const d = deaths.get(e.firstDeath.riotId) ?? { role: e.firstDeath.role, count: 0, ganked: 0 };
			d.count++;
			if (e.firstDeath.ganked) d.ganked++;
			deaths.set(e.firstDeath.riotId, d);
		}
		if (e.firstObjective) bump(first, e.firstObjective);
	}
	const at15 = tl.flatMap(g => (g.early!.goldDiff.at15 === undefined ? [] : [{ diff: g.early!.goldDiff.at15, win: g.us.win }]));
	const ahead = at15.filter(x => x.diff > 0);
	const behind = at15.filter(x => x.diff < 0);
	const dies = [...deaths].sort((a, b) => b[1].count - a[1].count)[0];
	const dragon = tl.flatMap(g => (g.early!.firstDragon ? [g.early!.firstDragon] : []));
	const jt = tl.flatMap(g => (g.early!.jungleTime ? [g.early!.jungleTime] : []));
	return {
		games: tl.length,
		jungle: {
			top: tl.filter(g => g.early!.jungleStart === "top").length,
			bot: tl.filter(g => g.early!.jungleStart === "bot").length,
			ganks: [...ganks].map(([lane, ts]) => ({ lane, count: ts.length })).sort((a, b) => b.count - a.count),
			gankT: ganks.size ? avg([...ganks.values()].flat()) : null
		},
		lvl1: { won: tl.filter(g => g.early!.lvl1 === "won").length, lost: tl.filter(g => g.early!.lvl1 === "lost").length },
		tempo: at15.length
			? { games: at15.length, ahead: ahead.length, aheadWins: ahead.filter(x => x.win).length, behind: behind.length, behindWins: behind.filter(x => x.win).length, avg: avg(at15.map(x => x.diff)) }
			: null,
		firstDeath:
			tl.length >= 2 && dies && dies[1].count >= 2
				? { riotId: dies[0], ...dies[1], deaths10: avg(tl.flatMap(g => g.us.players.filter(p => p.riotId === dies[0] && p.deaths10 !== undefined).map(p => p.deaths10!))) }
				: null,
		objectives: {
			first: [...first].map(([type, count]) => ({ type, count })).sort((a, b) => b.count - a.count),
			dragons: dragon.filter(d => d.ours).length,
			dragonGames: dragon.length,
			dragonT: dragon.some(d => d.ours) ? avg(dragon.filter(d => d.ours).map(d => d.t)) : null
		},
		presence: jt.length
			? { minutes: sum(jt.map(x => x.minutes)), top: sum(jt.map(x => x.top)), bot: sum(jt.map(x => x.bot)), invading: sum(jt.map(x => x.invading)) }
			: null,
		laneSwaps: tl.filter(g => g.early!.laneSwap).length,
		roams: ["MIDDLE", "UTILITY"].flatMap(role => {
			const rs = tl.flatMap(g => g.early!.roams.filter(r => r.role === role));
			const firsts = rs.flatMap(r => (r.first === undefined ? [] : [r.first]));
			return rs.length ? [{ role, games: rs.filter(r => r.minutes >= 2).length, minutes: avg(rs.map(r => r.minutes)), first: firsts.length ? avg(firsts) : null }] : [];
		})
	};
}

const MAP = 14800;
/** Red-side games mirrored over the river diagonal so the team's base is always bottom left; top lane stays top lane. */
export const fromBlue = (side: "blue" | "red", [x, y]: [number, number]): [number, number] => (side === "blue" ? [x, y] : [MAP - y, MAP - x]);

/** The team's Riot IDs as the PL pages list them, most games first. */
export function lineup(games: AnalysisGame[], max: number): string[] {
	const n = new Map<string, number>();
	for (const g of games) for (const p of g.us.players) n.set(p.riotId, (n.get(p.riotId) ?? 0) + 1);
	return [...n].sort((a, b) => b[1] - a[1]).slice(0, max).map(([id]) => id);
}
